import { normalizeLocale } from "@llmgateway/i18n";

import type { Locale } from "@llmgateway/i18n";
import type { Comparison } from "content-collections";

export interface SerializedFeatureRow {
	label: string;
	devpass: string | boolean;
	competitor: string | boolean;
	highlight?: boolean;
}

export interface SerializedFaqItem {
	question: string;
	answer: string;
}

export interface SerializedComparison {
	id: string;
	slug: string;
	locale?: Locale;
	date: string;
	draft?: boolean;
	title: string;
	metaTitle?: string;
	description: string;
	competitor: string;
	competitorTagline: string;
	tagline: string;
	devpassPrice: string;
	competitorPrice: string;
	verdict: string;
	features: SerializedFeatureRow[];
	faqs: SerializedFaqItem[];
	content: string;
}

export interface ComparisonSummary {
	id: string;
	slug: string;
	locale?: Locale;
	competitor: string;
	verdict: string;
}

export interface LocalizedComparisonGroup<T> {
	slug: string;
	en: T;
	zhCN?: T;
}

interface LocalizedEntry {
	slug: string;
	locale?: Locale | string;
}

export function getComparisonLocale(entry: LocalizedEntry): Locale {
	return normalizeLocale(entry.locale);
}

export function groupLocalizedComparisons<T extends LocalizedEntry>(
	entries: readonly T[],
): LocalizedComparisonGroup<T>[] {
	const groups = new Map<string, LocalizedComparisonGroup<T>>();

	for (const entry of entries) {
		const locale = getComparisonLocale(entry);
		const existing = groups.get(entry.slug);
		if (!existing) {
			groups.set(entry.slug, {
				slug: entry.slug,
				en: entry,
				zhCN: locale === "zh-CN" ? entry : undefined,
			});
			continue;
		}

		if (locale === "zh-CN") {
			existing.zhCN = entry;
		} else {
			existing.en = entry;
		}
	}

	return Array.from(groups.values());
}

export function getLocalizedComparison<T>(
	group: LocalizedComparisonGroup<T>,
	locale: Locale,
): T {
	return locale === "zh-CN" ? (group.zhCN ?? group.en) : group.en;
}

export function serializeComparison(entry: Comparison): SerializedComparison {
	return {
		id: entry.id,
		slug: entry.slug,
		locale: getComparisonLocale(entry),
		date: entry.date,
		draft: entry.draft,
		title: entry.title,
		metaTitle: entry.metaTitle,
		description: entry.description,
		competitor: entry.competitor,
		competitorTagline: entry.competitorTagline,
		tagline: entry.tagline,
		devpassPrice: entry.devpassPrice,
		competitorPrice: entry.competitorPrice,
		verdict: entry.verdict,
		features: entry.features,
		faqs: entry.faqs,
		content: entry.content,
	};
}

export function summarizeComparison(entry: Comparison): ComparisonSummary {
	return {
		id: entry.id,
		slug: entry.slug,
		locale: getComparisonLocale(entry),
		competitor: entry.competitor,
		verdict: entry.verdict,
	};
}
